import { StationeryTemplateCategoryEnum, useCardPdpDataQuery } from '@graphql/generated';
import { cloneDeep } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { findMatchingMarketingData, resetCardTemplateDefaults } from '../DesignsGallery/utils';
import { ValidDigitalDesignApiData } from '../DesignsGallery/DesignsGallery.types';
import { SAVE_THE_DATE_PHOTO_THEMES } from '@apps/saveTheDate/constants';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useStationeryProductDetailsPageHealth } from './ProductDetails.health';
import { useUpdatePdpUrlToIncludeTemplateId } from './useUpdatePdpUrlToIncludeTemplateId';
import { useAutofill } from '@apps/card/contexts/AutofillContext';

export type DigitalDesignTemplate = ValidDigitalDesignApiData;

type UsePdpDataArgs = {
  themeId: string;
  category: StationeryTemplateCategoryEnum;
  enableRedirectIfInvalid?: boolean;
  templateId?: Maybe<string>;
  enableUpdateUrlToIncludeTemplateId?: boolean;
};

export const usePdpData = (args: UsePdpDataArgs) => {
  const { themeId, category, templateId, enableRedirectIfInvalid = false, enableUpdateUrlToIncludeTemplateId = false } = args;
  const { redirectToCategoryShopAs404 } = useCardTelemetry();
  const { goToShop } = useCardsRouterContext();
  const {
    data,
    loading: dataLoading,
    error,
  } = useCardPdpDataQuery({
    batchMode: 'fast',
    variables: {
      themeId,
      category,
      skipDigitalDesign: category === StationeryTemplateCategoryEnum.thankYou,
    },
    ssr: true,
  });

  const { onLoadComplete, onLoadFailure } = useStationeryProductDetailsPageHealth();
  const { autofill, loading: autofillLoading } = useAutofill();
  const loading = dataLoading || autofillLoading;

  const template = useMemo(() => {
    const templates = data?.stationeryTemplatesByThemeId ?? [];
    const template = templates.find(template => template.id === (templateId ?? '')) ?? templates[0];
    if (!template) return null;

    const clonedTemplate = cloneDeep(template);
    resetCardTemplateDefaults(clonedTemplate);
    autofill(clonedTemplate, 'trackEvent');
    return clonedTemplate;
  }, [data?.stationeryTemplatesByThemeId, templateId, autofill]);

  useEffect(() => {
    if (error) {
      onLoadFailure(error);
    } else if (template?.id) {
      onLoadComplete();
    }
  }, [template, error, onLoadComplete, onLoadFailure]);

  useUpdatePdpUrlToIncludeTemplateId({
    themeId,
    templateCategory: category,
    templateMatches: data?.stationeryTemplatesByThemeId || [],
    enable: enableUpdateUrlToIncludeTemplateId,
  });

  const isPremiumDesign = !!template?.premium;

  const digitalTemplate: DigitalDesignTemplate | null = useMemo(() => {
    if (data?.ecardDesign) {
      const ecardDesign = data.ecardDesign;
      const maybeMatchingMarketingData = findMatchingMarketingData(ecardDesign, category);
      if (maybeMatchingMarketingData) {
        return {
          themeId: ecardDesign.themeId,
          name: ecardDesign.name,
          thumbnailUrl: maybeMatchingMarketingData.imageUrl,
          category: category as Extract<StationeryTemplateCategoryEnum, 'saveTheDate' | 'invitation'>,
          isPremiumDesign,
        };
      }
    }

    if (category === StationeryTemplateCategoryEnum.saveTheDate) {
      // TODO: photo ecards should be sent from card_service
      // https://withjoy.atlassian.net/browse/PRNT-1107
      const saveTheDateTheme = SAVE_THE_DATE_PHOTO_THEMES.find(theme => theme.themeId === themeId);
      if (saveTheDateTheme) {
        return {
          themeId: saveTheDateTheme.themeId,
          name: saveTheDateTheme.name,
          thumbnailUrl: saveTheDateTheme.thumbnailUrl,
          category,
          isPremiumDesign,
        };
      }
    }
    return null;
  }, [data, category, themeId, isPremiumDesign]);

  const hasTemplate = !!(template || digitalTemplate);
  const shouldRedirectBackToCategoryShop = enableRedirectIfInvalid && !hasTemplate && !loading;

  useEffect(() => {
    if (shouldRedirectBackToCategoryShop) {
      redirectToCategoryShopAs404({ page: 'pdp', themeId });
      goToShop('wedding', category);
    }
  }, [category, goToShop, redirectToCategoryShopAs404, themeId, shouldRedirectBackToCategoryShop]);

  return {
    loading,
    template,
    digitalTemplate,
  };
};
